import type { FilmManifest } from "./types";

/**
 * The timeline is authored in absolute film seconds. It intentionally contains
 * only original paraphrase or brief public-domain-like phrases; the references
 * identify the narrative source rather than claiming verbatim quotation from a
 * particular modern Bible translation.
 */
export const filmManifest: FilmManifest = {
  id: "the-veil-and-the-garden",
  title: "The Veil and the Garden",
  subtitle: "A cinematic meditation on the Passion and Resurrection",
  version: "1.0.0",
  locale: "en-US",
  runtimeSeconds: 490,
  frameRate: 24,
  resolution: { width: 1920, height: 1080 },
  captionFile: "/captions/en.vtt",
  openingCard: [
    "A dramatic theological interpretation",
    "The canonical Passion narratives are joined by clearly marked symbolic visions.",
    "This film is not a documentary reconstruction.",
  ],
  closingCard: [
    "Canonical foundation: Mark 14–16; Matthew 26–28; Luke 22–24; John 18–21; 1 Corinthians 15:3–8.",
    "Later mystical inspiration—not eyewitness history: Gospel of Thomas; Gospel of Truth.",
    "Also informing symbolic imagery: Treatise on the Resurrection; Gospel of Mary.",
    "No people or faith community bears collective guilt for the death of Jesus.",
  ],
  chapters: [
    {
      id: "the-veil",
      index: 1,
      title: "The Veil",
      start: 0,
      end: 45,
      duration: 45,
      card: {
        eyebrow: "I",
        title: "THE VEIL",
        subtitle: "Jerusalem · Passover",
      },
      setting: ["primordial dark", "memory fragments", "Jerusalem rooftop"],
      shots: [
        {
          id: "veil-void",
          start: 0,
          end: 7,
          classification: "VISION",
          reference: "Original symbolic prologue",
          rig: "tableau",
          lensMm: 50,
          from: [0, 0, 6],
          to: [0, 0, 5.5],
          target: [0, 0, 0],
          transition: "fade",
          exposure: -1.4,
          grade: "void-gold",
          description: "Blackness gathers into dust motes; the opening card floats behind a translucent veil.",
        },
        {
          id: "veil-water-bread",
          start: 7,
          end: 18,
          classification: "VISION",
          reference: "Original imagery; John 1:4–5 thematic echo",
          rig: "close",
          lensMm: 85,
          from: [0.2, 0.6, 2.8],
          to: [-0.2, 0.5, 2.2],
          target: [0, 0.2, 0],
          transition: "dissolve",
          exposure: -0.6,
          grade: "void-gold",
          description: "Dust becomes water on a hand, then flour and a round of bread.",
          depthOfField: { focusDistance: 2.2, aperture: 1.8 },
        },
        {
          id: "veil-memory",
          start: 18,
          end: 30,
          classification: "VISION",
          reference: "Composite memory of Jesus's ministry",
          rig: "dolly",
          lensMm: 40,
          from: [-2, 1.4, 5],
          to: [1.5, 1.4, 3.8],
          target: [0, 1.1, 0],
          transition: "light-wipe",
          exposure: 0,
          grade: "void-gold",
          description: "Fragmentary silhouettes share bread, wash feet, embrace, and rise from dust.",
        },
        {
          id: "veil-jerusalem",
          start: 30,
          end: 42,
          classification: "COMPOSITE",
          reference: "Passover setting across Mark 14; Matthew 26; Luke 22; John 13",
          rig: "crane",
          lensMm: 28,
          from: [0, 18, 22],
          to: [0, 11, 15],
          target: [0, 2, 0],
          transition: "match-cut",
          exposure: -0.1,
          grade: "passover-amber",
          description: "A single ember match-cuts to a city of Passover lamps beneath the moon.",
        },
        {
          id: "veil-window",
          start: 42,
          end: 45,
          classification: "COMPOSITE",
          reference: "Mark 14:12–25; Matthew 26:17–29; Luke 22:7–23; John 13:1–30",
          rig: "dolly",
          lensMm: 65,
          from: [2.4, 2, 6],
          to: [2.1, 1.8, 4.2],
          target: [0, 1.4, 0],
          transition: "dissolve",
          exposure: -0.3,
          grade: "passover-amber",
          description: "The camera finds one lit upper-room window; the chapter card resolves in its smoke.",
        },
      ],
      visualCues: [
        {
          id: "veil-forms",
          start: 1,
          end: 17,
          classification: "VISION",
          reference: "Original symbolic imagery",
          action: "Dust, water, flour, and lamplight emerge from darkness as connected forms.",
          motif: "living-light",
          intensity: 0.45,
        },
        {
          id: "veil-fear",
          start: 18,
          end: 30,
          classification: "VISION",
          reference: "Original reflection; Gospel of Truth thematic influence",
          action: "A gauze-like fog crosses memories of mercy without obscuring the human figures completely.",
          motif: "fog",
          intensity: 0.4,
        },
        {
          id: "veil-city",
          start: 30,
          end: 45,
          classification: "COMPOSITE",
          reference: "Mark 14:12–17; Matthew 26:17–20; Luke 22:7–14",
          action: "Jerusalem glows with household lamps while Roman patrol silhouettes cross the lower streets.",
          motif: "fire",
          intensity: 0.55,
        },
      ],
      dialogue: [
        {
          id: "mary-veil-01",
          start: 8,
          end: 15,
          speaker: "Mary Magdalene",
          text: "Before we learned the names of fear, light moved through everything.",
          classification: "VISION",
          reference: "Original narration; John 1:4–5 thematic echo",
          performance: "Intimate, grounded; wonder without breathiness.",
          audioFile: "/audio/dialogue/mary-veil-01.mp3",
          subtitlePhrases: [
            { start: 8, end: 10.482, text: "Before we learned the names of fear," },
            { start: 10.522, end: 13.439, text: "light moved through everything." },
          ],
        },
        {
          id: "mary-veil-02",
          start: 18,
          end: 25.5,
          speaker: "Mary Magdalene",
          text: "We covered it with power, shame, and certainty, then called the darkness real.",
          classification: "VISION",
          reference: "Original narration; Gospel of Truth thematic influence",
          performance: "Clear and unsentimental, with quiet grief on 'darkness.'",
          audioFile: "/audio/dialogue/mary-veil-02.mp3",
          subtitlePhrases: [
            { start: 18, end: 21.699, text: "We covered it with power, shame, and certainty," },
            { start: 21.739, end: 25.085, text: "then called the darkness real." },
          ],
        },
        {
          id: "mary-veil-03",
          start: 32,
          end: 40.5,
          speaker: "Mary Magdalene",
          text: "But in Jerusalem, on the night of Passover, the light sat down among us and broke bread.",
          classification: "COMPOSITE",
          reference: "Mark 14:12–25; Matthew 26:17–29; Luke 22:7–23; John 13:1–30",
          performance: "Measured remembrance; allow warmth to enter on 'among us.'",
          audioFile: "/audio/dialogue/mary-veil-03.mp3",
          subtitlePhrases: [
            { start: 32, end: 34.944, text: "But in Jerusalem, on the night of Passover," },
            { start: 34.984, end: 39.574, text: "the light sat down among us and broke bread." },
          ],
        },
      ],
      transitionOut: "A city lamp becomes the upper room's central flame.",
    },
    {
      id: "bread-and-night",
      index: 2,
      title: "Bread and Night",
      start: 45,
      end: 100,
      duration: 55,
      card: { eyebrow: "II", title: "BREAD AND NIGHT", subtitle: "The upper room" },
      setting: ["upper room", "stairwell", "Gethsemane threshold"],
      shots: [
        {
          id: "bread-tableau",
          start: 45,
          end: 56,
          classification: "COMPOSITE",
          reference: "Mark 14:17–25; Matthew 26:20–29; Luke 22:14–27; John 13:1–17",
          rig: "tableau",
          lensMm: 32,
          from: [0, 2.3, 8],
          to: [0, 2.1, 7.2],
          target: [0, 1.1, 0],
          transition: "dissolve",
          exposure: -0.2,
          grade: "passover-amber",
          description: "A low table, thirteen distinct figures, and a basin share the warm center of the frame.",
        },
        {
          id: "bread-basin",
          start: 56,
          end: 66,
          classification: "CANON",
          reference: "John 13:3–17",
          rig: "close",
          lensMm: 70,
          from: [-1.5, 1, 2.8],
          to: [0.2, 0.8, 2.3],
          target: [0, 0.35, 0],
          transition: "cut",
          exposure: 0,
          grade: "passover-amber",
          description: "Jesus dries a disciple's feet; water carries firelight across the floor.",
          depthOfField: { focusDistance: 2.1, aperture: 2 },
        },
        {
          id: "bread-breaking",
          start: 66,
          end: 83,
          classification: "COMPOSITE",
          reference: "Mark 14:22–25; Matthew 26:26–29; Luke 22:14–20",
          rig: "dolly",
          lensMm: 55,
          from: [1.8, 1.4, 4.2],
          to: [0.8, 1.3, 3],
          target: [0, 1.1, 0],
          transition: "match-cut",
          exposure: 0.1,
          grade: "passover-amber",
          description: "Bread breaks in close-up; wine fills a shared cup; faces register love and unease.",
        },
        {
          id: "bread-judas",
          start: 83,
          end: 96,
          classification: "COMPOSITE",
          reference: "Matthew 26:20–25; John 13:21–30",
          rig: "close",
          lensMm: 85,
          from: [-2.5, 1.7, 3.5],
          to: [-1.8, 1.65, 2.8],
          target: [-0.8, 1.35, 0],
          transition: "cut",
          exposure: -0.45,
          grade: "passover-amber",
          description: "Judas is neither monstrous nor isolated from humanity; fear and resolve cross his face.",
          depthOfField: { focusDistance: 2.5, aperture: 1.8 },
        },
        {
          id: "bread-moon",
          start: 96,
          end: 100,
          classification: "COMPOSITE",
          reference: "John 13:30; Mark 14:32",
          rig: "crane",
          lensMm: 40,
          from: [0, 2, 5],
          to: [0, 8, 11],
          target: [0, 1, 0],
          transition: "match-cut",
          exposure: -0.8,
          grade: "garden-blue",
          description: "A gust extinguishes Judas's lamp; poured wine match-cuts to blue moonlight on olive leaves.",
        },
      ],
      visualCues: [
        {
          id: "bread-washing",
          start: 49,
          end: 65,
          classification: "CANON",
          reference: "John 13:3–17",
          action: "Jesus kneels to wash feet; no disciple is visually ranked above another.",
          motif: "water",
          intensity: 0.7,
        },
        {
          id: "bread-elements",
          start: 66,
          end: 83,
          classification: "CANON",
          reference: "Mark 14:22–25; Matthew 26:26–29; Luke 22:14–20",
          action: "Bread and cup pass hand to hand around the table.",
          motif: "bread",
          intensity: 0.75,
        },
        {
          id: "bread-departure",
          start: 84,
          end: 99,
          classification: "COMPOSITE",
          reference: "Matthew 26:20–25; John 13:21–30",
          action: "Judas leaves by choice into a crowded city night; the wind, not an entity, takes his flame.",
          motif: "fire",
          intensity: 0.35,
        },
      ],
      dialogue: [
        {
          id: "jesus-bread-01",
          start: 50,
          end: 55,
          speaker: "Jesus",
          text: "Do you understand what I have done for you?",
          classification: "CANON",
          reference: "John 13:12",
          performance: "Quiet, attentive, still slightly winded from kneeling.",
          audioFile: "/audio/dialogue/jesus-bread-01.mp3",
          subtitlePhrases: [{ start: 50, end: 53.166, text: "Do you understand what I have done for you?" }],
        },
        {
          id: "jesus-bread-02",
          start: 57,
          end: 63,
          speaker: "Jesus",
          text: "I have given you an example. Become servants to one another.",
          classification: "COMPOSITE",
          reference: "John 13:14–17; Luke 22:26–27",
          performance: "Warm instruction, never declamatory.",
          audioFile: "/audio/dialogue/jesus-bread-02.mp3",
          subtitlePhrases: [
            { start: 57, end: 58.367, text: "I have given you an example." },
            { start: 58.407, end: 60.96, text: "Become servants to one another." },
          ],
        },
        {
          id: "jesus-bread-03",
          start: 67,
          end: 72.5,
          speaker: "Jesus",
          text: "Take this bread. It is my body, given for you. Remember me.",
          classification: "COMPOSITE",
          reference: "Mark 14:22; Matthew 26:26; Luke 22:19",
          performance: "Concrete and tender; a long breath after 'body.'",
          audioFile: "/audio/dialogue/jesus-bread-03.mp3",
          subtitlePhrases: [
            { start: 67, end: 69.199, text: "Take this bread. It is my body," },
            { start: 69.239, end: 71.936, text: "given for you. Remember me." },
          ],
        },
        {
          id: "jesus-bread-04",
          start: 75,
          end: 81.5,
          speaker: "Jesus",
          text: "Share this cup, the covenant poured out for many. I will drink it new with you.",
          classification: "COMPOSITE",
          reference: "Mark 14:23–25; Matthew 26:27–29; Luke 22:20",
          performance: "Solemn but reassuring; lift gently on the final promise.",
          audioFile: "/audio/dialogue/jesus-bread-04.mp3",
          subtitlePhrases: [
            { start: 75, end: 77.98, text: "Share this cup, the covenant poured out for many." },
            { start: 78.02, end: 80.43, text: "I will drink it new with you." },
          ],
        },
        {
          id: "judas-bread-01",
          start: 85,
          end: 88,
          speaker: "Judas",
          text: "Surely it is not I, teacher?",
          classification: "CANON",
          reference: "Matthew 26:25",
          performance: "Barely controlled; a question asked despite knowing the answer.",
          audioFile: "/audio/dialogue/judas-bread-01.mp3",
          subtitlePhrases: [{ start: 85, end: 87.242, text: "Surely it is not I, teacher?" }],
        },
        {
          id: "jesus-bread-05",
          start: 89,
          end: 91.5,
          speaker: "Jesus",
          text: "You have said it.",
          classification: "CANON",
          reference: "Matthew 26:25",
          performance: "Soft grief; no accusation.",
          audioFile: "/audio/dialogue/jesus-bread-05.mp3",
          subtitlePhrases: [{ start: 89, end: 90.65, text: "You have said it." }],
        },
        {
          id: "jesus-bread-06",
          start: 93,
          end: 96,
          speaker: "Jesus",
          text: "What you are doing, do quickly.",
          classification: "CANON",
          reference: "John 13:27",
          performance: "Private, resigned, spoken across the room.",
          audioFile: "/audio/dialogue/jesus-bread-06.mp3",
          subtitlePhrases: [{ start: 93, end: 95.329, text: "What you are doing, do quickly." }],
        },
      ],
      transitionOut: "Wine becomes moonlight; room tone becomes the breath of olive leaves.",
    },
    {
      id: "garden-and-judgment",
      index: 3,
      title: "The Garden and Judgment",
      start: 100,
      end: 155,
      duration: 55,
      card: { eyebrow: "III", title: "THE GARDEN AND JUDGMENT", subtitle: "Night before Rome" },
      setting: ["Gethsemane", "courtyard", "Roman judgment pavement"],
      shots: [
        {
          id: "garden-prayer",
          start: 100,
          end: 119,
          classification: "COMPOSITE",
          reference: "Mark 14:32–42; Matthew 26:36–46; Luke 22:39–46",
          rig: "handheld",
          lensMm: 65,
          from: [2.5, 1.4, 4.5],
          to: [0.8, 1.1, 2.4],
          target: [0, 0.9, 0],
          transition: "dissolve",
          exposure: -0.7,
          grade: "garden-blue",
          description: "Formal symmetry yields to a trembling close frame as Jesus kneels and struggles to breathe.",
          handheldStrength: 0.2,
          depthOfField: { focusDistance: 2.2, aperture: 2 },
        },
        {
          id: "garden-arrest",
          start: 119,
          end: 133,
          classification: "COMPOSITE",
          reference: "Mark 14:43–50; Matthew 26:47–56; Luke 22:47–53; John 18:1–11",
          rig: "pov",
          lensMm: 35,
          from: [0, 1.7, 5],
          to: [0, 1.7, 2.2],
          target: [0, 1.4, 0],
          transition: "cut",
          exposure: -0.4,
          grade: "garden-blue",
          description: "Torchlight splinters between trees; an arresting party closes around Jesus and his followers.",
          handheldStrength: 0.3,
        },
        {
          id: "garden-denial",
          start: 133,
          end: 145,
          classification: "COMPOSITE",
          reference: "Mark 14:66–72; Matthew 26:69–75; Luke 22:54–62; John 18:15–27",
          rig: "close",
          lensMm: 85,
          from: [-1.3, 1.7, 3],
          to: [-0.6, 1.7, 2.5],
          target: [0, 1.5, 0],
          transition: "cut",
          exposure: -0.35,
          grade: "judgment-stone",
          description: "Peter denies Jesus beside a charcoal fire; across the courtyard their eyes meet in silence.",
          depthOfField: { focusDistance: 2.3, aperture: 1.6 },
        },
        {
          id: "garden-pilate",
          start: 145,
          end: 155,
          classification: "COMPOSITE",
          reference: "Mark 15:1–15; Matthew 27:11–26; Luke 23:1–25; John 18:28–19:16",
          rig: "tableau",
          lensMm: 45,
          from: [0, 2.2, 7],
          to: [0.5, 2, 5.5],
          target: [0, 1.4, 0],
          transition: "cut",
          exposure: 0.05,
          grade: "judgment-stone",
          description: "Roman standards dominate the frame; Pilate weighs political utility, then exercises imperial authority.",
        },
      ],
      visualCues: [
        {
          id: "garden-agony",
          start: 101,
          end: 119,
          classification: "CANON",
          reference: "Mark 14:33–36; Luke 22:41–44",
          action: "Jesus trembles, sweats, and presses both hands into the earth; his companions sleep nearby.",
          motif: "fog",
          intensity: 0.65,
        },
        {
          id: "garden-seizure",
          start: 119,
          end: 133,
          classification: "COMPOSITE",
          reference: "Mark 14:43–50; Matthew 26:47–56; Luke 22:47–53; John 18:1–11",
          action: "Temple personnel and Roman force are shown as separate groups converging under institutional orders.",
          motif: "fire",
          intensity: 0.8,
        },
        {
          id: "garden-denial-cue",
          start: 133,
          end: 145,
          classification: "COMPOSITE",
          reference: "Mark 14:66–72; Matthew 26:69–75; Luke 22:54–62; John 18:15–27",
          action: "A servant recognizes Peter; his third denial is followed by a rooster and a silent exchange of looks.",
          motif: "fire",
          intensity: 0.3,
        },
        {
          id: "garden-judgment-cue",
          start: 145,
          end: 155,
          classification: "COMPOSITE",
          reference: "Mark 15:1–15; Matthew 27:11–26; Luke 23:1–25; John 18:28–19:16",
          action: "Pilate's soldiers, seal, and elevated chair make Roman responsibility visually unambiguous.",
          motif: "shadow-power",
          intensity: 0.75,
        },
      ],
      dialogue: [
        {
          id: "jesus-garden-01",
          start: 104,
          end: 110,
          speaker: "Jesus",
          text: "Father, if you are willing, take this cup from me.",
          classification: "CANON",
          reference: "Luke 22:42; Mark 14:36; Matthew 26:39",
          performance: "Human fear, short of breath; no ceremonial cadence.",
          audioFile: "/audio/dialogue/jesus-garden-01.mp3",
          subtitlePhrases: [{ start: 104, end: 107.823, text: "Father, if you are willing, take this cup from me." }],
        },
        {
          id: "jesus-garden-02",
          start: 112,
          end: 117,
          speaker: "Jesus",
          text: "Yet not my will. Let your will be done.",
          classification: "CANON",
          reference: "Luke 22:42; Matthew 26:39",
          performance: "A decision made through fear, not after fear disappears.",
          audioFile: "/audio/dialogue/jesus-garden-02.mp3",
          subtitlePhrases: [{ start: 112, end: 115.34, text: "Yet not my will. Let your will be done." }],
        },
        {
          id: "judas-garden-01",
          start: 122,
          end: 124.5,
          speaker: "Judas",
          text: "Greetings, teacher.",
          classification: "CANON",
          reference: "Matthew 26:49",
          performance: "Forced steadiness collapsing into shame.",
          audioFile: "/audio/dialogue/judas-garden-01.mp3",
          subtitlePhrases: [{ start: 122, end: 124.5, text: "Greetings, teacher." }],
        },
        {
          id: "jesus-garden-03",
          start: 125,
          end: 128,
          speaker: "Jesus",
          text: "Friend, do what you came to do.",
          classification: "CANON",
          reference: "Matthew 26:50",
          performance: "Direct compassion, spoken only to Judas.",
          audioFile: "/audio/dialogue/jesus-garden-03.mp3",
          subtitlePhrases: [{ start: 125, end: 127.425, text: "Friend, do what you came to do." }],
        },
        {
          id: "peter-denial-01",
          start: 136,
          end: 140,
          speaker: "Peter",
          text: "I do not know the man you mean.",
          classification: "COMPOSITE",
          reference: "Mark 14:71; Matthew 26:72–74; Luke 22:60; John 18:25–27",
          performance: "Too loud, defensive, immediately regretted.",
          audioFile: "/audio/dialogue/peter-denial-01.mp3",
          subtitlePhrases: [{ start: 136, end: 139.03, text: "I do not know the man you mean." }],
        },
        {
          id: "pilate-judgment-01",
          start: 146,
          end: 150,
          speaker: "Pilate",
          text: "Are you the king of the Jews?",
          classification: "CANON",
          reference: "Mark 15:2; Matthew 27:11; Luke 23:3; John 18:33",
          performance: "Clipped and administrative, testing a political claim.",
          audioFile: "/audio/dialogue/pilate-judgment-01.mp3",
          subtitlePhrases: [{ start: 146, end: 148.442, text: "Are you the king of the Jews?" }],
        },
        {
          id: "jesus-judgment-01",
          start: 151,
          end: 154,
          speaker: "Jesus",
          text: "You say so.",
          classification: "CANON",
          reference: "Mark 15:2; Matthew 27:11; Luke 23:3",
          performance: "Exhausted, calm, neither evasive nor defiant.",
          audioFile: "/audio/dialogue/jesus-judgment-01.mp3",
          subtitlePhrases: [{ start: 151, end: 153.33, text: "You say so." }],
        },
      ],
      transitionOut: "Pilate's seal strikes wax; the impact becomes a crossbeam hitting stone.",
    },
    {
      id: "golgotha",
      index: 4,
      title: "Golgotha",
      start: 155,
      end: 270,
      duration: 115,
      card: { eyebrow: "IV", title: "GOLGOTHA", subtitle: "The place of a skull" },
      setting: ["Jerusalem road", "city gate", "Golgotha"],
      shots: [
        {
          id: "golgotha-road",
          start: 155,
          end: 181,
          classification: "COMPOSITE",
          reference: "Mark 15:20–22; Matthew 27:31–33; Luke 23:26–32; John 19:16–17",
          rig: "handheld",
          lensMm: 28,
          from: [-2, 0.8, 6],
          to: [2, 0.65, 4],
          target: [0, 1.2, 0],
          transition: "cut",
          exposure: 0.2,
          grade: "golgotha-dust",
          description: "The road is low, crowded, and intermittently subjective; soldiers create the corridor of force.",
          handheldStrength: 0.45,
        },
        {
          id: "golgotha-raised",
          start: 181,
          end: 205,
          classification: "COMPOSITE",
          reference: "Mark 15:20–26; Matthew 27:31–37; Luke 23:26–34; John 19:16–24",
          rig: "crane",
          lensMm: 40,
          from: [0, 1.2, 5],
          to: [0, 5, 9],
          target: [0, 2.2, 0],
          transition: "dissolve",
          exposure: 0.05,
          grade: "golgotha-dust",
          description: "The cross is raised mostly in silhouette; physical cost is carried by breath, weight, and witnesses' faces.",
        },
        {
          id: "golgotha-witnesses",
          start: 205,
          end: 223,
          classification: "COMPOSITE",
          reference: "Luke 23:39–43; John 19:25–27",
          rig: "close",
          lensMm: 85,
          from: [2.8, 1.6, 4],
          to: [1.2, 1.55, 3],
          target: [0, 1.5, 0],
          transition: "cut",
          exposure: -0.05,
          grade: "golgotha-dust",
          description: "Mary Magdalene anchors a group of Jewish mourners; soldiers and distant spectators remain distinct.",
          depthOfField: { focusDistance: 2.8, aperture: 2 },
        },
        {
          id: "golgotha-powers",
          start: 223,
          end: 246,
          classification: "VISION",
          reference: "Original symbolism; Colossians 2:15 and Gospel of Truth thematic influence",
          rig: "pov",
          lensMm: 50,
          from: [0, 2.5, 4],
          to: [0, 2.5, 2.8],
          target: [0, 2.4, 0],
          transition: "dissolve",
          exposure: -0.5,
          grade: "void-gold",
          description: "Abstract shadows form from whispered accusation, rank, fear, and imperial insignia—never literal creatures.",
        },
        {
          id: "golgotha-death",
          start: 246,
          end: 263,
          classification: "COMPOSITE",
          reference: "Mark 15:33–39; Matthew 27:45–54; Luke 23:44–49; John 19:28–37",
          rig: "close",
          lensMm: 70,
          from: [1.4, 2.8, 4],
          to: [0.7, 2.7, 3],
          target: [0, 2.5, 0],
          transition: "cut",
          exposure: -0.25,
          grade: "golgotha-dust",
          description: "Jesus's retained humanity fills the frame; after his final breath, the vision vanishes into plain daylight.",
          depthOfField: { focusDistance: 2.6, aperture: 2.4 },
        },
        {
          id: "golgotha-wind",
          start: 263,
          end: 270,
          classification: "VISION",
          reference: "Original transition grounded in Gospel witness accounts",
          rig: "tableau",
          lensMm: 35,
          from: [0, 3, 10],
          to: [0, 3, 11],
          target: [0, 2.5, 0],
          transition: "fade",
          exposure: 0,
          grade: "golgotha-dust",
          description: "No supernatural figure remains—only three crosses, ordinary wind, grieving witnesses, and Roman guards.",
        },
      ],
      visualCues: [
        {
          id: "golgotha-procession",
          start: 156,
          end: 181,
          classification: "COMPOSITE",
          reference: "Mark 15:20–22; Matthew 27:31–33; Luke 23:26–32; John 19:16–17",
          action: "Jesus falls once; Simon of Cyrene is compelled to carry the beam; mourners follow at a distance.",
          motif: "fog",
          intensity: 0.55,
        },
        {
          id: "golgotha-crucifixion",
          start: 181,
          end: 205,
          classification: "COMPOSITE",
          reference: "Mark 15:22–32; Matthew 27:33–44; Luke 23:32–43; John 19:17–27",
          action: "Nails and wounds are implied through sound, hands gripping wood, cloth, breath, and reaction—not explicit gore.",
          motif: "wound",
          intensity: 0.7,
        },
        {
          id: "golgotha-mercy",
          start: 205,
          end: 223,
          classification: "CANON",
          reference: "Luke 23:39–43",
          action: "One condemned man asks to be remembered; Jesus turns toward him as far as his body permits.",
          motif: "living-light",
          intensity: 0.35,
        },
        {
          id: "golgotha-archons",
          start: 223,
          end: 246,
          classification: "VISION",
          reference: "Original symbolic sequence",
          action: "Words of accusation become dark ribbons tied to coins, armor, and status; refusal to hate loosens every knot.",
          motif: "shadow-power",
          intensity: 0.9,
        },
        {
          id: "golgotha-last-breath",
          start: 246,
          end: 263,
          classification: "COMPOSITE",
          reference: "Mark 15:33–39; Matthew 27:45–54; Luke 23:44–49; John 19:28–37",
          action: "The symbolic shadows disappear before the last breath; death remains bodily, exposed, and witnessed.",
          motif: "wound",
          intensity: 0.6,
        },
      ],
      dialogue: [
        {
          id: "soldier-road-01",
          start: 159,
          end: 161.5,
          speaker: "Roman soldier",
          text: "Keep moving.",
          classification: "VISION",
          reference: "Original line within the canonical procession",
          performance: "Routine command, not theatrical cruelty.",
          audioFile: "/audio/dialogue/soldier-road-01.mp3",
          subtitlePhrases: [{ start: 159, end: 160.748, text: "Keep moving." }],
        },
        {
          id: "jesus-road-01",
          start: 170,
          end: 178,
          speaker: "Jesus",
          text: "Daughters of Jerusalem, do not weep for me. Weep for yourselves and for your children.",
          classification: "CANON",
          reference: "Luke 23:28",
          performance: "Labored but lucid; compassion outruns pain.",
          audioFile: "/audio/dialogue/jesus-road-01.mp3",
          subtitlePhrases: [
            { start: 170, end: 172.936, text: "Daughters of Jerusalem, do not weep for me." },
            { start: 172.976, end: 175.879, text: "Weep for yourselves and for your children." },
          ],
        },
        {
          id: "jesus-cross-01",
          start: 189,
          end: 195,
          speaker: "Jesus",
          text: "Father, forgive them. They do not know what they are doing.",
          classification: "CANON",
          reference: "Luke 23:34, textually variant in early manuscripts",
          performance: "Broken by breath; addressed upward, never sentimental.",
          audioFile: "/audio/dialogue/jesus-cross-01.mp3",
          subtitlePhrases: [
            { start: 189, end: 190.092, text: "Father, forgive them." },
            { start: 190.132, end: 193.099, text: "They do not know what they are doing." },
          ],
        },
        {
          id: "condemned-cross-01",
          start: 205,
          end: 210,
          speaker: "Condemned man",
          text: "Jesus, remember me when you come into your kingdom.",
          classification: "CANON",
          reference: "Luke 23:42",
          performance: "Faint, stripped of bravado.",
          audioFile: "/audio/dialogue/condemned-cross-01.mp3",
          subtitlePhrases: [{ start: 205, end: 208.222, text: "Jesus, remember me when you come into your kingdom." }],
        },
        {
          id: "jesus-cross-02",
          start: 211,
          end: 217,
          speaker: "Jesus",
          text: "Today, you will be with me in paradise.",
          classification: "CANON",
          reference: "Luke 23:43",
          performance: "Painfully slow; certainty without triumphalism.",
          audioFile: "/audio/dialogue/jesus-cross-02.mp3",
          subtitlePhrases: [{ start: 211, end: 214.41, text: "Today, you will be with me in paradise." }],
        },
        {
          id: "mary-cross-01",
          start: 224,
          end: 230,
          speaker: "Mary Magdalene",
          text: "We had called him teacher. Rome called him a warning.",
          classification: "VISION",
          reference: "Original narration grounded in Roman crucifixion context",
          performance: "Controlled shock; emphasize the contrast, not anger.",
          audioFile: "/audio/dialogue/mary-cross-01.mp3",
          subtitlePhrases: [
            { start: 224, end: 225.753, text: "We had called him teacher." },
            { start: 225.793, end: 228.451, text: "Rome called him a warning." },
          ],
        },
        {
          id: "jesus-cross-03",
          start: 236,
          end: 242,
          speaker: "Jesus",
          text: "My God, my God, why have you forsaken me?",
          classification: "CANON",
          reference: "Mark 15:34; Matthew 27:46; Psalm 22:1",
          performance: "Raw abandonment; the first words almost fail.",
          audioFile: "/audio/dialogue/jesus-cross-03.mp3",
          subtitlePhrases: [{ start: 236, end: 239.346, text: "My God, my God, why have you forsaken me?" }],
        },
        {
          id: "jesus-cross-04",
          start: 249,
          end: 253,
          speaker: "Jesus",
          text: "It is finished.",
          classification: "CANON",
          reference: "John 19:30",
          performance: "Almost a whisper; release, not victory cry.",
          audioFile: "/audio/dialogue/jesus-cross-04.mp3",
          subtitlePhrases: [{ start: 249, end: 251.441, text: "It is finished." }],
        },
        {
          id: "mary-cross-02",
          start: 258,
          end: 266,
          speaker: "Mary Magdalene",
          text: "The shadows wanted a spectacle. He gave them only mercy, and his final breath.",
          classification: "VISION",
          reference: "Original symbolic narration",
          performance: "Hushed and concrete; finish without music under the final word.",
          audioFile: "/audio/dialogue/mary-cross-02.mp3",
          subtitlePhrases: [
            { start: 258, end: 260.462, text: "The shadows wanted a spectacle." },
            { start: 260.502, end: 266, text: "He gave them only mercy, and his final breath." },
          ],
        },
      ],
      transitionOut: "Wind lifts burial linen into frame; bright cloth fades to the tomb's dusk.",
    },
    {
      id: "silence",
      index: 5,
      title: "Silence",
      start: 270,
      end: 330,
      duration: 60,
      card: { eyebrow: "V", title: "SILENCE", subtitle: "Sabbath" },
      setting: ["garden tomb", "sealed room", "empty Jerusalem", "symbolic interior"],
      shots: [
        {
          id: "silence-burial",
          start: 270,
          end: 288,
          classification: "COMPOSITE",
          reference: "Mark 15:42–47; Matthew 27:57–61; Luke 23:50–56; John 19:38–42",
          rig: "dolly",
          lensMm: 55,
          from: [2.5, 1.4, 5],
          to: [1.1, 1.2, 3.2],
          target: [0, 0.8, 0],
          transition: "dissolve",
          exposure: -0.45,
          grade: "sabbath-ash",
          description: "Hands wrap a genuinely weighty body in linen before sunset; the camera does not look away from grief.",
        },
        {
          id: "silence-stone",
          start: 288,
          end: 298,
          classification: "COMPOSITE",
          reference: "Mark 15:42–47; Matthew 27:57–61; Luke 23:50–56; John 19:38–42",
          rig: "tableau",
          lensMm: 40,
          from: [0, 1.8, 7],
          to: [0, 1.8, 6.2],
          target: [0, 1.2, 0],
          transition: "cut",
          exposure: -0.7,
          grade: "sabbath-ash",
          description: "The stone rolls shut with unhurried physical force; sunset disappears from the entrance.",
        },
        {
          id: "silence-sabbath",
          start: 298,
          end: 312,
          classification: "VISION",
          reference: "Original Sabbath montage grounded in Luke 23:56",
          rig: "tableau",
          lensMm: 65,
          from: [0, 1.7, 5],
          to: [0, 1.7, 4.5],
          target: [0, 1.3, 0],
          transition: "dissolve",
          exposure: -0.9,
          grade: "sabbath-ash",
          description: "Mary, Peter, Jesus's mother, and an empty street occupy separate pools of stillness.",
        },
        {
          id: "silence-descent",
          start: 312,
          end: 325,
          classification: "VISION",
          reference: "Original symbolic sequence; Treatise on the Resurrection thematic influence",
          rig: "pov",
          lensMm: 50,
          from: [0, 1.6, 4],
          to: [0, 1.6, 1.8],
          target: [0, 1.4, 0],
          transition: "fade",
          exposure: -1,
          grade: "void-gold",
          description: "A human silhouette passes through memories of shame and accusation in a nonliteral inner landscape.",
        },
        {
          id: "silence-first-light",
          start: 325,
          end: 330,
          classification: "VISION",
          reference: "Original transition to resurrection dawn",
          rig: "close",
          lensMm: 85,
          from: [0, 1.5, 2.5],
          to: [0, 1.5, 2.2],
          target: [0, 1.4, 0],
          transition: "light-wipe",
          exposure: -0.3,
          grade: "resurrection-pearl",
          description: "When the silhouette recognizes each accusation as powerless, the shadows become morning mist.",
        },
      ],
      visualCues: [
        {
          id: "silence-entombment",
          start: 271,
          end: 298,
          classification: "COMPOSITE",
          reference: "Mark 15:42–47; Matthew 27:57–61; Luke 23:50–56; John 19:38–42",
          action: "Joseph, Nicodemus, and women witnesses share the burial labor before sundown.",
          motif: "wound",
          intensity: 0.4,
        },
        {
          id: "silence-grief",
          start: 298,
          end: 312,
          classification: "VISION",
          reference: "Original montage; Luke 23:56 anchors Sabbath rest",
          action: "The city continues around private grief; no character receives privileged knowledge.",
          motif: "fog",
          intensity: 0.25,
        },
        {
          id: "silence-inner-passage",
          start: 312,
          end: 330,
          classification: "VISION",
          reference: "Original non-geographic vision",
          action: "Memories of failure dissolve when seen without shame; this is inner symbolism, not a map of hell.",
          motif: "shadow-power",
          intensity: 0.6,
        },
      ],
      dialogue: [
        {
          id: "joseph-silence-01",
          start: 274,
          end: 277,
          speaker: "Joseph of Arimathea",
          text: "Bring the linen. Gently.",
          classification: "VISION",
          reference: "Original line within John 19:38–42 burial scene",
          performance: "Practical tenderness, spoken through fatigue.",
          audioFile: "/audio/dialogue/joseph-silence-01.mp3",
          subtitlePhrases: [{ start: 274, end: 276.439, text: "Bring the linen. Gently." }],
        },
        {
          id: "mary-silence-01",
          start: 299,
          end: 306,
          speaker: "Mary Magdalene",
          text: "We thought the kingdom had been buried with him. We did not yet understand the seed.",
          classification: "VISION",
          reference: "Original narration; John 12:24 thematic echo",
          performance: "Nearly dry of tears; the last word opens slightly.",
          audioFile: "/audio/dialogue/mary-silence-01.mp3",
          subtitlePhrases: [
            { start: 299, end: 301.536, text: "We thought the kingdom had been buried with him." },
            { start: 301.576, end: 304.986, text: "We did not yet understand the seed." },
          ],
        },
        {
          id: "mary-silence-02",
          start: 314,
          end: 322,
          speaker: "Mary Magdalene",
          text: "In the hidden chamber of grief, every accusing voice wore a familiar face.",
          classification: "VISION",
          reference: "Original narration; Gospel of Mary thematic influence",
          performance: "Dreamlike but intelligible; neither horror nor certainty.",
          audioFile: "/audio/dialogue/mary-silence-02.mp3",
          subtitlePhrases: [
            { start: 314, end: 315.7, text: "In the hidden chamber of grief," },
            { start: 315.74, end: 319.323, text: "every accusing voice wore a familiar face." },
          ],
        },
      ],
      transitionOut: "The last shadow becomes mist over the tomb garden at dawn.",
    },
    {
      id: "garden-reopened",
      index: 6,
      title: "The Garden Reopened",
      start: 330,
      end: 405,
      duration: 75,
      card: { eyebrow: "VI", title: "THE GARDEN REOPENED", subtitle: "The first day" },
      setting: ["tomb garden", "tomb interior", "dawn orchard"],
      shots: [
        {
          id: "reopened-approach",
          start: 330,
          end: 347,
          classification: "COMPOSITE",
          reference: "Mark 16:1–4; Matthew 28:1–4; Luke 24:1–3; John 20:1",
          rig: "dolly",
          lensMm: 35,
          from: [-4, 1.5, 8],
          to: [-1.5, 1.4, 5],
          target: [0, 1.1, 0],
          transition: "dissolve",
          exposure: -0.35,
          grade: "resurrection-pearl",
          description: "Women approach through predawn blue; the open tomb is discovered without spectacle.",
        },
        {
          id: "reopened-empty",
          start: 347,
          end: 360,
          classification: "COMPOSITE",
          reference: "Mark 16:5–7; Matthew 28:5–7; Luke 24:4–8; John 20:11–13",
          rig: "pov",
          lensMm: 45,
          from: [0, 1.6, 4],
          to: [0, 1.5, 2],
          target: [0, 1, 0],
          transition: "cut",
          exposure: -0.1,
          grade: "resurrection-pearl",
          description: "Linen retains the shape of absence; voices arrive in an acoustically impossible stillness.",
        },
        {
          id: "reopened-gardener",
          start: 360,
          end: 377,
          classification: "CANON",
          reference: "John 20:11–16",
          rig: "close",
          lensMm: 70,
          from: [2.4, 1.7, 4],
          to: [1, 1.65, 3],
          target: [0, 1.5, 0],
          transition: "dissolve",
          exposure: 0.1,
          grade: "resurrection-pearl",
          description: "Mary sees a backlit gardener; recognition waits entirely on her name.",
          depthOfField: { focusDistance: 2.5, aperture: 1.8 },
        },
        {
          id: "reopened-recognition",
          start: 377,
          end: 392,
          classification: "VISION",
          reference: "Original inverted-light treatment within John 20:16–18",
          rig: "close",
          lensMm: 85,
          from: [-1, 1.7, 3],
          to: [-0.5, 1.7, 2.6],
          target: [0, 1.55, 0],
          transition: "light-wipe",
          exposure: 0.2,
          grade: "resurrection-pearl",
          description: "Ordinary dawn subtly lights shadowed surfaces first; Jesus retains wounds and impossible calm.",
          depthOfField: { focusDistance: 2.3, aperture: 1.6 },
        },
        {
          id: "reopened-running",
          start: 392,
          end: 405,
          classification: "VISION",
          reference: "Original veins-of-light transition within Matthew 28:8–10 and John 20:17–18",
          rig: "crane",
          lensMm: 32,
          from: [0, 2, 6],
          to: [0, 9, 13],
          target: [0, 1, 0],
          transition: "match-cut",
          exposure: 0.25,
          grade: "resurrection-pearl",
          description: "Mary runs toward the city; garden paths briefly resemble veins of light, then return to soil.",
        },
      ],
      visualCues: [
        {
          id: "reopened-stone",
          start: 331,
          end: 347,
          classification: "COMPOSITE",
          reference: "Mark 16:1–6; Matthew 28:1–7; Luke 24:1–8; John 20:1–13",
          action: "The stone already stands open when the women arrive; no action-movie removal is shown.",
          motif: "fog",
          intensity: 0.35,
        },
        {
          id: "reopened-linen",
          start: 347,
          end: 360,
          classification: "COMPOSITE",
          reference: "Luke 24:12; John 20:5–7",
          action: "Linen, stone, and retained wounds emphasize bodily continuity amid mystery.",
          motif: "wound",
          intensity: 0.4,
        },
        {
          id: "reopened-name",
          start: 360,
          end: 392,
          classification: "VISION",
          reference: "Original acoustic and inverted-light interpretation within John 20:11–18",
          action: "The sound field widens when Jesus says Mary's name; the light source remains natural yet behaves subtly inside-out.",
          motif: "living-light",
          intensity: 0.8,
        },
        {
          id: "reopened-witness",
          start: 392,
          end: 405,
          classification: "COMPOSITE",
          reference: "Matthew 28:8–10; John 20:17–18",
          action: "Mary becomes the first commissioned witness in the film's narrative emphasis.",
          motif: "living-light",
          intensity: 0.65,
        },
      ],
      dialogue: [
        {
          id: "mary-tomb-01",
          start: 337,
          end: 341,
          speaker: "Mary Magdalene",
          text: "Who has moved the stone?",
          classification: "VISION",
          reference: "Original line within Mark 16:3 and John 20:1 context",
          performance: "Alarm held low to avoid waking the garden.",
          audioFile: "/audio/dialogue/mary-tomb-01.mp3",
          subtitlePhrases: [{ start: 337, end: 339.27, text: "Who has moved the stone?" }],
        },
        {
          id: "messenger-tomb-01",
          start: 350,
          end: 356,
          speaker: "Messenger",
          text: "Why do you seek the living among the dead? He has risen.",
          classification: "CANON",
          reference: "Luke 24:5–6",
          performance: "Human warmth with uncanny acoustic clarity; never booming.",
          audioFile: "/audio/dialogue/messenger-tomb-01.mp3",
          subtitlePhrases: [
            { start: 350, end: 352.353, text: "Why do you seek the living among the dead?" },
            { start: 352.393, end: 354.63, text: "He has risen." },
          ],
        },
        {
          id: "mary-tomb-02",
          start: 364,
          end: 369,
          speaker: "Mary Magdalene",
          text: "Sir, if you carried him away, tell me where you laid him.",
          classification: "CANON",
          reference: "John 20:15",
          performance: "Urgent courtesy through tears; she genuinely sees a gardener.",
          audioFile: "/audio/dialogue/mary-tomb-02.mp3",
          subtitlePhrases: [{ start: 364, end: 368.81, text: "Sir, if you carried him away, tell me where you laid him." }],
        },
        {
          id: "jesus-tomb-01",
          start: 373,
          end: 375,
          speaker: "Jesus",
          text: "Mary.",
          classification: "CANON",
          reference: "John 20:16",
          performance: "Her name as recognition, familiar and embodied.",
          audioFile: "/audio/dialogue/jesus-tomb-01.mp3",
          subtitlePhrases: [{ start: 373, end: 373.601, text: "Mary." }],
        },
        {
          id: "mary-tomb-03",
          start: 377,
          end: 379.5,
          speaker: "Mary Magdalene",
          text: "Rabbouni!",
          classification: "CANON",
          reference: "John 20:16",
          performance: "A spontaneous cry of recognition, not polished speech.",
          audioFile: "/audio/dialogue/mary-tomb-03.mp3",
          subtitlePhrases: [{ start: 377, end: 379.057, text: "Rabbouni!" }],
        },
        {
          id: "jesus-tomb-02",
          start: 382,
          end: 389,
          speaker: "Jesus",
          text: "Do not hold on to me. Go to my brothers and tell them I am ascending to my Father and yours.",
          classification: "CANON",
          reference: "John 20:17",
          performance: "Gentle boundary, followed by a clear commission.",
          audioFile: "/audio/dialogue/jesus-tomb-02.mp3",
          subtitlePhrases: [
            { start: 382, end: 383.469, text: "Do not hold on to me." },
            { start: 383.509, end: 389, text: "Go to my brothers and tell them I am ascending to my Father and yours." },
          ],
        },
        {
          id: "mary-tomb-04",
          start: 397,
          end: 401,
          speaker: "Mary Magdalene",
          text: "I have seen the Lord.",
          classification: "CANON",
          reference: "John 20:18",
          performance: "Breathless, certain, offered rather than imposed.",
          audioFile: "/audio/dialogue/mary-tomb-04.mp3",
          subtitlePhrases: [{ start: 397, end: 399.23, text: "I have seen the Lord." }],
        },
      ],
      transitionOut: "Mary's running feet match-cut to disciples pacing inside a locked room.",
    },
    {
      id: "eyes-opened",
      index: 7,
      title: "Eyes Opened",
      start: 405,
      end: 490,
      duration: 85,
      card: { eyebrow: "VII", title: "EYES OPENED", subtitle: "Jerusalem · Emmaus · Galilee" },
      setting: ["locked room", "Emmaus road", "shoreline", "mountain", "shared world"],
      shots: [
        {
          id: "eyes-testimony",
          start: 405,
          end: 428,
          classification: "COMPOSITE",
          reference: "Mark 16:9–11; Luke 24:9–11; John 20:18–19",
          rig: "handheld",
          lensMm: 50,
          from: [1.8, 1.7, 5],
          to: [0.8, 1.65, 3.6],
          target: [0, 1.4, 0],
          transition: "cut",
          exposure: -0.2,
          grade: "sabbath-ash",
          description: "Mary's certainty meets the disciples' fear; no camera cue undermines her testimony.",
          handheldStrength: 0.12,
        },
        {
          id: "eyes-emmaus",
          start: 428,
          end: 442,
          classification: "CANON",
          reference: "Luke 24:13–35",
          rig: "dolly",
          lensMm: 40,
          from: [-3, 1.7, 7],
          to: [1, 1.6, 5],
          target: [0, 1.3, 0],
          transition: "dissolve",
          exposure: 0.15,
          grade: "galilee-dawn",
          description: "Travelers walk beside an unrecognized companion; breaking bread opens a flash of recognition.",
        },
        {
          id: "eyes-thomas",
          start: 442,
          end: 459,
          classification: "CANON",
          reference: "John 20:24–29",
          rig: "close",
          lensMm: 85,
          from: [1.8, 1.7, 3.4],
          to: [0.9, 1.65, 2.6],
          target: [0, 1.45, 0],
          transition: "match-cut",
          exposure: 0.05,
          grade: "resurrection-pearl",
          description: "Thomas faces the wounds without shame; Jesus offers evidence rather than rebuke.",
          depthOfField: { focusDistance: 2.3, aperture: 1.8 },
        },
        {
          id: "eyes-peter",
          start: 459,
          end: 471,
          classification: "CANON",
          reference: "John 21:9–19",
          rig: "close",
          lensMm: 65,
          from: [-2, 1.5, 4],
          to: [-0.8, 1.45, 3],
          target: [0, 1.3, 0],
          transition: "dissolve",
          exposure: 0.1,
          grade: "galilee-dawn",
          description: "Beside another charcoal fire, Peter receives restoration as work: love expressed through care.",
        },
        {
          id: "eyes-ascension",
          start: 471,
          end: 481,
          classification: "COMPOSITE",
          reference: "Luke 24:50–53; Acts 1:6–11; Matthew 28:16–20",
          rig: "crane",
          lensMm: 28,
          from: [0, 1.8, 7],
          to: [0, 12, 15],
          target: [0, 2, 0],
          transition: "light-wipe",
          exposure: 0.3,
          grade: "fullness",
          description: "The camera rises with light, then deliberately turns downward toward the waiting human faces.",
        },
        {
          id: "eyes-world",
          start: 481,
          end: 490,
          classification: "VISION",
          reference: "Original epilogue; Luke 24:30–35 thematic echo",
          rig: "tableau",
          lensMm: 45,
          from: [0, 2.2, 8],
          to: [0, 2, 7],
          target: [0, 1.2, 0],
          transition: "fade",
          exposure: 0.2,
          grade: "fullness",
          description: "Bread, water, wounds, and many human faces share one living light before the credits enter darkness.",
        },
      ],
      visualCues: [
        {
          id: "eyes-doubt",
          start: 406,
          end: 428,
          classification: "COMPOSITE",
          reference: "Mark 16:9–11; Luke 24:9–11; John 20:18–19",
          action: "Disciples doubt from grief and fear; Mary remains centered, composed, and fully human.",
          motif: "fog",
          intensity: 0.35,
        },
        {
          id: "eyes-bread",
          start: 428,
          end: 442,
          classification: "CANON",
          reference: "Luke 24:13–35",
          action: "Recognition arrives through hospitality and broken bread, then refuses possession.",
          motif: "bread",
          intensity: 0.65,
        },
        {
          id: "eyes-wounds",
          start: 442,
          end: 459,
          classification: "CANON",
          reference: "John 20:24–29",
          action: "Resurrection does not erase wounds; they are visible without being fetishized.",
          motif: "wound",
          intensity: 0.5,
        },
        {
          id: "eyes-restoration",
          start: 459,
          end: 471,
          classification: "CANON",
          reference: "John 21:9–19",
          action: "The denial fire is echoed by a breakfast fire; Peter is restored through a commission to care.",
          motif: "fire",
          intensity: 0.55,
        },
        {
          id: "eyes-return",
          start: 471,
          end: 490,
          classification: "VISION",
          reference: "Original epilogue; Luke 24:50–53, Acts 1:6–11, and Matthew 28:16–20 thematic foundation",
          action: "Ascending light returns to human faces, soil, food, and water: transcendence reveals creation rather than abandoning it.",
          motif: "living-light",
          intensity: 1,
        },
      ],
      dialogue: [
        {
          id: "disciple-eyes-01",
          start: 409,
          end: 414,
          speaker: "Disciple",
          text: "Mary, grief can make the heart see what it longs for.",
          classification: "VISION",
          reference: "Original line grounded in Luke 24:11 and Mark 16:11",
          performance: "Concerned, not contemptuous; fear disguised as reason.",
          audioFile: "/audio/dialogue/disciple-eyes-01.mp3",
          subtitlePhrases: [{ start: 409, end: 412.914, text: "Mary, grief can make the heart see what it longs for." }],
        },
        {
          id: "mary-eyes-01",
          start: 417,
          end: 424,
          speaker: "Mary Magdalene",
          text: "I know grief. I also know the voice that spoke my name.",
          classification: "VISION",
          reference: "Original dialogue grounded in John 20:16–18",
          performance: "Patient certainty; refuse to argue for the experience.",
          audioFile: "/audio/dialogue/mary-eyes-01.mp3",
          subtitlePhrases: [
            { start: 417, end: 418.46, text: "I know grief." },
            { start: 418.5, end: 422.15, text: "I also know the voice that spoke my name." },
          ],
        },
        {
          id: "emmaus-eyes-01",
          start: 431,
          end: 436,
          speaker: "Emmaus traveler",
          text: "Stay with us. Evening is near.",
          classification: "CANON",
          reference: "Luke 24:29",
          performance: "Simple hospitality after a long walk.",
          audioFile: "/audio/dialogue/emmaus-eyes-01.mp3",
          subtitlePhrases: [{ start: 431, end: 433.437, text: "Stay with us. Evening is near." }],
        },
        {
          id: "thomas-eyes-01",
          start: 443,
          end: 448,
          speaker: "Thomas",
          text: "Unless I see the wounds, I will not believe.",
          classification: "CANON",
          reference: "John 20:25",
          performance: "Honest anguish, not cynical swagger.",
          audioFile: "/audio/dialogue/thomas-eyes-01.mp3",
          subtitlePhrases: [{ start: 443, end: 446.293, text: "Unless I see the wounds, I will not believe." }],
        },
        {
          id: "jesus-eyes-01",
          start: 450,
          end: 456,
          speaker: "Jesus",
          text: "See my hands. Bring your hand near. Do not be afraid to believe.",
          classification: "COMPOSITE",
          reference: "John 20:27; Luke 24:39",
          performance: "Inviting and unoffended; a gift of evidence.",
          audioFile: "/audio/dialogue/jesus-eyes-01.mp3",
          subtitlePhrases: [
            { start: 450, end: 452.577, text: "See my hands. Bring your hand near." },
            { start: 452.617, end: 454.934, text: "Do not be afraid to believe." },
          ],
        },
        {
          id: "peter-eyes-01",
          start: 460,
          end: 464,
          speaker: "Peter",
          text: "Lord, you know that I love you.",
          classification: "CANON",
          reference: "John 21:15–17",
          performance: "Shame and love at once; eyes fixed on the fire.",
          audioFile: "/audio/dialogue/peter-eyes-01.mp3",
          subtitlePhrases: [{ start: 460, end: 462.937, text: "Lord, you know that I love you." }],
        },
        {
          id: "jesus-eyes-02",
          start: 465,
          end: 468,
          speaker: "Jesus",
          text: "Then feed my sheep.",
          classification: "CANON",
          reference: "John 21:17",
          performance: "A commission, gentle and practical.",
          audioFile: "/audio/dialogue/jesus-eyes-02.mp3",
          subtitlePhrases: [{ start: 465, end: 467.094, text: "Then feed my sheep." }],
        },
        {
          id: "mary-eyes-02",
          start: 474,
          end: 487,
          speaker: "Mary Magdalene",
          text: "Resurrection was not an escape from the world. It was the veil lifting, and creation becoming visible as gift.",
          classification: "VISION",
          reference: "Original final narration; Gospel of Thomas 3 and Gospel of Truth thematic influence",
          performance: "Expansive but intimate; land the final word in near-silence.",
          audioFile: "/audio/dialogue/mary-eyes-02.mp3",
          subtitlePhrases: [
            { start: 474, end: 476.812, text: "Resurrection was not an escape from the world." },
            { start: 476.852, end: 479.205, text: "It was the veil lifting," },
            { start: 479.245, end: 482.555, text: "and creation becoming visible as gift." },
          ],
        },
      ],
      transitionOut: "The living light contracts to a shared lamp, then fades into source credits.",
    },
  ],
  cast: [
    {
      character: "Jesus",
      voiceId: "UNASSIGNED_STOCK_VOICE",
      model: "eleven_multilingual_v2",
      description: "Warm restrained baritone; bodily, intimate, never oracular.",
      settings: { stability: 0.62, similarityBoost: 0.78, style: 0.18, useSpeakerBoost: true },
      pronunciationRules: ["Gethsemane: geth-SEM-uh-nee", "Rabbouni: rab-BOO-nee"],
    },
    {
      character: "Mary Magdalene",
      voiceId: "UNASSIGNED_STOCK_VOICE",
      model: "eleven_multilingual_v2",
      description: "Grounded contralto with emotional precision; also carries narration.",
      settings: { stability: 0.58, similarityBoost: 0.8, style: 0.22, useSpeakerBoost: true },
      pronunciationRules: ["Magdalene: MAG-duh-leen", "Rabbouni: rab-BOO-nee"],
    },
    {
      character: "Peter",
      voiceId: "UNASSIGNED_STOCK_VOICE",
      model: "eleven_multilingual_v2",
      description: "Weathered baritone; impulsive strength with accessible vulnerability.",
      settings: { stability: 0.55, similarityBoost: 0.76, style: 0.24, useSpeakerBoost: true },
      pronunciationRules: [],
    },
    {
      character: "Judas",
      voiceId: "UNASSIGNED_STOCK_VOICE",
      model: "eleven_multilingual_v2",
      description: "Tense vulnerable tenor; recognizable as a frightened human being.",
      settings: { stability: 0.5, similarityBoost: 0.76, style: 0.28, useSpeakerBoost: true },
      pronunciationRules: [],
    },
    {
      character: "Pilate",
      voiceId: "UNASSIGNED_STOCK_VOICE",
      model: "eleven_multilingual_v2",
      description: "Controlled clipped low tenor; political calculation rather than melodrama.",
      settings: { stability: 0.7, similarityBoost: 0.74, style: 0.12, useSpeakerBoost: true },
      pronunciationRules: ["Pilate: PIE-luht"],
    },
    {
      character: "Ensemble",
      voiceId: "UNASSIGNED_STOCK_VOICE",
      model: "eleven_multilingual_v2",
      description: "Distinct supporting voices for messengers, travelers, witnesses, and Roman soldiers.",
      settings: { stability: 0.64, similarityBoost: 0.7, style: 0.15, useSpeakerBoost: true },
      pronunciationRules: ["Arimathea: air-uh-muh-THEE-uh", "Emmaus: eh-MAY-us"],
    },
  ],
  sources: [
    {
      id: "mark-passion",
      title: "The Gospel according to Mark, chapters 14–16",
      url: "https://www.biblegateway.com/passage/?search=Mark+14-16&version=NRSVUE",
      usage: "Canonical Passion and empty-tomb narrative.",
      category: "canonical",
    },
    {
      id: "matthew-passion",
      title: "The Gospel according to Matthew, chapters 26–28",
      url: "https://www.biblegateway.com/passage/?search=Matthew+26-28&version=NRSVUE",
      usage: "Canonical Passion, burial, and resurrection narrative.",
      category: "canonical",
    },
    {
      id: "luke-passion",
      title: "The Gospel according to Luke, chapters 22–24",
      url: "https://www.biblegateway.com/passage/?search=Luke+22-24&version=NRSVUE",
      usage: "Canonical Passion, Emmaus, and Ascension narrative.",
      category: "canonical",
    },
    {
      id: "john-passion",
      title: "The Gospel according to John, chapters 18–21",
      url: "https://www.biblegateway.com/passage/?search=John+18-21&version=NRSVUE",
      usage: "Canonical Passion, Mary in the garden, Thomas, and Peter's restoration.",
      category: "canonical",
    },
    {
      id: "paul-resurrection",
      title: "First Corinthians 15:3–8",
      url: "https://www.biblegateway.com/passage/?search=1+Corinthians+15%3A3-8&version=NRSVUE",
      usage: "Early canonical resurrection witness tradition.",
      category: "canonical",
    },
    {
      id: "gospel-thomas",
      title: "Gospel of Thomas",
      usage: "Later mystical thematic influence only; never presented as eyewitness history.",
      category: "mystical",
    },
    {
      id: "gospel-truth",
      title: "Gospel of Truth",
      usage: "Later imagery of ignorance, error, and recognition; identified as interpretive.",
      category: "mystical",
    },
    {
      id: "treatise-resurrection",
      title: "Treatise on the Resurrection",
      usage: "Later thematic influence on the symbolic interior sequence.",
      category: "mystical",
    },
    {
      id: "gospel-mary",
      title: "Gospel of Mary",
      usage: "Later thematic influence on grief, accusation, and Mary as witness.",
      category: "mystical",
    },
    {
      id: "nostra-aetate",
      title: "Nostra Aetate",
      url: "https://www.vatican.va/content/dam/wss/archive/hist_councils/ii_vatican_council/documents/vat-ii_decl_19651028_nostra-aetate_en.html",
      usage: "Guardrail against collective Jewish guilt and antisemitic Passion framing.",
      category: "sensitivity",
    },
    {
      id: "usccb-passion-guidelines",
      title: "USCCB Criteria for the Evaluation of Dramatizations of the Passion",
      url: "https://www.usccb.org/prayer-and-worship/liturgical-year/lent/guidelines-for-catholic-jewish-relations",
      usage: "Historical and sensitivity review criteria.",
      category: "sensitivity",
    },
  ],
  sensitivity: {
    framing:
      "Rome's state power performs the execution. Individual Jewish authorities, followers, mourners, pilgrims, and dissenters are differentiated; no collective Jewish guilt is suggested.",
    contentAdvisory:
      "PG-13: crucifixion, grief, restrained wound imagery, execution sounds, and themes of betrayal and death; no explicit gore.",
    guardrails: [
      "Never use 'the Jews' as a collective antagonist in dialogue, captions, staging, or metadata.",
      "Keep Roman military authority and Pilate's legal power visually explicit.",
      "Do not code virtue or villainy through skin tone, accent, facial structure, or costume cleanliness.",
      "Depict Judas as morally responsible but fully human; do not add demonic possession imagery.",
      "Keep mystical powers abstract and institution-linked, never mapped to an ethnicity or living faith.",
      "Frame violence through bodily cost and witness response, not spectacle or gore.",
      "Label harmonized chronology COMPOSITE and all invented or later-text-inspired material VISION.",
    ],
    reviewChecklist: [
      "Roman responsibility remains legible in every trial and execution shot.",
      "Crowds contain visibly varied reactions: mourning, fear, indifference, protest, and coercion.",
      "Women witnesses retain agency and are not reduced to reaction shots.",
      "No VISION event is presented in voiceover as settled historical fact.",
      "Resurrection retains bodily continuity through linen, wounds, voice, food, and touch.",
      "Source and interpretation cards remain readable in the opening and credits.",
    ],
  },
};

export default filmManifest;
